import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockBannerCompactProps } from '@ts-blocks/block-banner-compact/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

const CTA_FALLBACK_TEXT = 'Try for free'

interface BlockBannerCompactHookOptions {
  block: () => BlockBannerCompactProps
}

interface BlockBannerCompactHookReturn {
  refCtaText: ComputedRef<string>
  refCtaHref: ComputedRef<string>
  refCtaTarget: ComputedRef<'_self' | '_blank'>
  handleClickCta: () => void
}

export const useBlockBannerCompact = ({ block }: BlockBannerCompactHookOptions): BlockBannerCompactHookReturn => {
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'banner-compact' })

  const refCtaText = computed<string>(() => block().button?.buttonText || CTA_FALLBACK_TEXT)

  const refBaseUrl = computed<string>(() => {
    const button = block().button
    if (button && button.isDefaultUrl === false) return button.buttonLink || refDefaultCtaUrl.value
    return refDefaultCtaUrl.value
  })

  const refCtaHref = computed<string>(() => buildCtaHref(refBaseUrl.value, { text: refCtaText.value }))

  const refCtaTarget = computed<'_self' | '_blank'>(() => (
    refBaseUrl.value === refDefaultCtaUrl.value ? '_self' : '_blank'
  ))

  const handleClickCta = (): void => {
    trackSectionEvent('Button_click', { name: 'banner_cta', text: refCtaText.value, url: refCtaHref.value })
  }

  return {
    refCtaText,
    refCtaHref,
    refCtaTarget,
    handleClickCta,
  }
}
